import { Dayjs } from "dayjs";
import { memo, useCallback, useMemo } from "react";
import { isToday } from "src/utils";

interface IProps {
    day: Dayjs | null;
    selectedDate: Dayjs | null | undefined;
    index: number;

    today: Dayjs;
    month: number;
    selectDay: (date: Dayjs) => void;
    getAvailableDay: (day: Dayjs) => boolean;
    getScheduledDay: (day: Dayjs) => boolean;
    isPatientScheduling: boolean;
}

const DAY_CLASS_BASE = "cal-day";

const CalendarDay: React.FC<IProps> = memo(
    ({
        day,
        selectedDate,
        index,
        today,
        selectDay,
        getAvailableDay,
        getScheduledDay,
        isPatientScheduling,
    }) => {
        const klass = useMemo((): string => {
            if (!day) {
                return DAY_CLASS_BASE;
            }
            const isScheduledDay: boolean = getScheduledDay(day);
            const isAvailableDay: boolean = isScheduledDay && getAvailableDay(day);

            const _isToday = isToday(day);
            const isSelected =
                selectedDate &&
                day.date() === selectedDate.date() &&
                day.month() === selectedDate.month() &&
                day.year() === selectedDate.year();

            const _base = _isToday ? `${DAY_CLASS_BASE} today` : DAY_CLASS_BASE;
            const base = isSelected ? `${_base} selected` : _base;

            if (!isScheduledDay) {
                return base;
            }

            const schedulable = `${base} scheduable`;
            if (day.isBefore(today)) {
                if (isPatientScheduling) {
                    return base;
                }
                return schedulable;
            }

            if (!isAvailableDay) {
                return schedulable;
            }

            const available = `${schedulable} available`;
            if (!isSelected) {
                return available;
            }
            return `${available} selected`;
        }, [day, getScheduledDay, getAvailableDay, selectedDate, today, isPatientScheduling]);

        const renderTodayCircle = useMemo(() => {
            if (isToday(day)) {
                return <div className={"today"} />;
            }
            return null;
        }, [day]);

        const dayOfMonth = useMemo(() => day?.date(), [day]);
        const onSelectDay = useCallback(() => {
            if (day) {
                selectDay(day);
            }
        }, [day, selectDay]);

        // const onClick = isAvailableDay ? () => selectDay(day) : noop;
        if (!day) {
            return <td key={`calendar-empty-${index}`}>{""}</td>;
        }

        return (
            <td className={`${klass} z-1`} onClick={onSelectDay}>
                <button className={klass} id={`cy-${dayOfMonth}`}>
                    <span>{dayOfMonth}</span>
                    {renderTodayCircle}
                </button>
            </td>
        );
    },
    (previousProps: IProps, nextProps: IProps) => {
        return !!(
            previousProps.selectedDate &&
            nextProps.selectedDate &&
            previousProps.selectedDate.isSame(nextProps.selectedDate) &&
            previousProps.day &&
            nextProps.day &&
            previousProps.day.isSame(nextProps.day) &&
            previousProps.getScheduledDay(previousProps.day) &&
            nextProps.getScheduledDay(nextProps.day) &&
            previousProps.getAvailableDay(previousProps.day) &&
            nextProps.getAvailableDay(nextProps.day)
        );
    },
);

export default CalendarDay;
